// 📁 lib/scheduling/types.ts
// Type definitions for the scheduling system

export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';

export interface TimeSlot {
  start: string; // 24-hour format: "09:00"
  end: string;   // 24-hour format: "17:00"
}

export interface DailyAvailability {
  enabled: boolean;
  slots: TimeSlot[];
}

export interface WeeklySchedule {
  monday: DailyAvailability;
  tuesday: DailyAvailability;
  wednesday: DailyAvailability;
  thursday: DailyAvailability;
  friday: DailyAvailability;
  saturday: DailyAvailability;
  sunday: DailyAvailability;
}

export interface ExpertScheduleSettings {
  expertId: string;
  weeklySchedule: WeeklySchedule;
  timezone: string; // e.g., "America/New_York"
  bookingBuffer: number; // minutes before/after bookings
  minAdvanceBooking: number; // minimum hours in advance for bookings
  maxAdvanceBooking: number; // maximum days in advance for bookings
  defaultCallDuration: number; // default duration in minutes (15, 30, 60)
  allowedDurations: number[]; // allowed durations: [15, 30, 60]
  instantCallsEnabled: boolean; // allow instant calls when online
  scheduledCallsEnabled: boolean; // allow scheduled bookings
  createdAt: any;
  updatedAt: any;
}

export interface DateOverride {
  id: string;
  expertId: string;
  date: string; // ISO date string: "2025-01-15"
  type: 'blocked' | 'custom_hours';
  reason?: string; // "vacation", "holiday", "personal"
  customSlots?: TimeSlot[];
  createdAt: any;
}

export interface Booking {
  id: string;
  expertId: string;
  expertName: string;
  expertEmail?: string; // expert's email for reminder notifications
  expertRate: number;
  questerId: string;
  questerName: string;
  questerEmail: string;
  scheduledStartTime: any; // Firestore timestamp
  scheduledEndTime: any; // Firestore timestamp
  duration: number; // minutes
  estimatedCost: number;
  timezone: string;
  status: BookingStatus;
  callType: 'audio' | 'video';
  notes?: string; // quester notes/questions
  meetingLink?: string; // generated call link (for external video meetings)
  meetingLocation?: string; // location address (for in-person meetings)
  meetingPlatform?: string; // 'camyvera', 'external', or 'in-person'
  meetingInstructions?: string; // instructions for joining
  reminderSent?: boolean;
  cancelledBy?: string; // userId who cancelled
  cancellationReason?: string;
  actualStartTime?: any;
  actualEndTime?: any;
  actualCost?: number;
  actualDuration?: number;
  callLogId?: string; // link to call log after completion
  seekerRating?: number | null;
  seekerTotalReviews?: number;
  createdAt: any;
  updatedAt: any;
}

export interface AvailableSlot {
  start: Date;
  end: Date;
  duration: number;
  available: boolean;
}

export interface BookingNotification {
  id: string;
  bookingId: string;
  userId: string;
  type: 'booking_created' | 'booking_confirmed' | 'booking_reminder' | 'booking_cancelled' | 'booking_started';
  message: string;
  read: boolean;
  createdAt: any;
}

// Default schedule: Monday-Friday 9am-5pm
export const DEFAULT_SCHEDULE: WeeklySchedule = {
  monday: {
    enabled: true,
    slots: [{ start: '09:00', end: '17:00' }]
  },
  tuesday: {
    enabled: true,
    slots: [{ start: '09:00', end: '17:00' }]
  },
  wednesday: {
    enabled: true,
    slots: [{ start: '09:00', end: '17:00' }]
  },
  thursday: {
    enabled: true,
    slots: [{ start: '09:00', end: '17:00' }]
  },
  friday: {
    enabled: true,
    slots: [{ start: '09:00', end: '17:00' }]
  },
  saturday: {
    enabled: false,
    slots: []
  },
  sunday: {
    enabled: false,
    slots: []
  }
};

export const DEFAULT_SCHEDULE_SETTINGS: Partial<ExpertScheduleSettings> = {
  bookingBuffer: 15, // 15 minutes between calls
  minAdvanceBooking: 2, // minimum 2 hours advance notice
  maxAdvanceBooking: 30, // maximum 30 days in advance
  defaultCallDuration: 30, // default 30-minute calls
  allowedDurations: [15, 30, 60], // 15, 30, or 60 minute calls
  instantCallsEnabled: true,
  scheduledCallsEnabled: true,
  timezone: 'America/New_York'
};
